import dynamic from "next/dynamic";
import { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { EntityFormSectionConfig } from "@/lib/types/forms/entity-form-sections";
import type { Tickets } from "@/src/gql/graphql";
import { ENHANCED_TICKET_DETAIL_CONFIG } from "./TicketDetailConfig";

/**
 * Loaded lazily, NOT statically: TicketConfig sits inside the entity-registry
 * module graph, and TicketBulkEditModal pulls in the UniversalCRMTable
 * runtime (SimpleTableContext, unified-field-renderer), which circles back
 * into the registry. A static import closes that cycle and crashed the whole
 * prod client bundle with a minified TDZ ReferenceError ("Cannot access 'T'
 * before initialization"). dynamic() defers evaluation to render time, which
 * breaks the cycle; the modal only loads when a user opens bulk edit.
 */
const TicketBulkEditModal = dynamic<{
  selectedItems: Tickets[];
  isOpen: boolean;
  onClose: () => void;
  clearSelection?: () => void;
}>(
  () =>
    import("@/components/shared/BulkActions/TicketBulkEditModal").then(
      (m) => m.TicketBulkEditModal
    ),
  { loading: () => null }
);
import { TICKET_QUICK_FILTERS } from "@/components/shared/config/quick-filters/domain/ticket-filters";

// Import ticket fields
import { ticketFields } from "../config/fields";

// Import ticket configuration
import { ticketSavedViews } from "../config/saved-views";
import { ticketFeatures } from "../config/features";

/** Logical order for create ticket form: template → what/who → status → assignment/dates → notes/tags → options */
const CREATE_TICKET_FIELD_ORDER: string[] = [
  "template",
  "title",
  "description",
  "contact_id",
  "status",
  "priority",
  "category",
  "assigned_to",
  "due_date",
  "estimated_hours",
  "tags",
  "internal_notes",
  "customer_visible_notes",
  "is_internal",
  "auto_assign",
  "auto_escalate",
  "sla_hours",
  "escalation_level",
  "total_billed",
];

/**
 * Progressive-disclosure section layout for the Create Ticket form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
const CREATE_TICKET_FORM_SECTIONS = (): readonly EntityFormSectionConfig[] => [
  {
    id: "core",
    kind: "always",
    fieldNames: ["template", "title", "status", "priority"],
  },
  {
    id: "relationships",
    kind: "collapsible",
    summary: "Link to a contact and assignee",
    fieldNames: ["contact_id", "assigned_to"],
  },
  {
    id: "scheduling",
    kind: "collapsible",
    summary: "Due date and estimated hours",
    fieldNames: ["due_date", "estimated_hours"],
  },
  {
    id: "description",
    kind: "collapsible",
    title: "Description & notes",
    fieldNames: ["description", "internal_notes", "customer_visible_notes"],
  },
  {
    id: "classification",
    kind: "collapsible",
    summary: "Category and tags",
    fieldNames: ["category", "tags"],
  },
  {
    id: "automation",
    kind: "collapsible",
    title: "Automation & SLA",
    summary: "Routing, escalation, SLA, billing",
    fieldNames: [
      "is_internal",
      "auto_assign",
      "auto_escalate",
      "sla_hours",
      "escalation_level",
      "total_billed",
    ],
  },
];

/**
 * Unified Ticket Configuration
 * This single configuration serves both table display and form creation/editing
 * Eliminates duplication between multiple ticket config files
 */
export const TICKET_CONFIG: SimpleTableConfig<Tickets> = {
  entity: "tickets",
  displayName: "Ticket",
  statusFieldId: "status",
  defaultPageSize: 10,
  boardViewPageSize: 100,
  defaultSort: { field: "created_at", direction: "desc" },
  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,
  autoIncludeUserId: true, // Auto-fill created_by with current user

  createFormFieldOrder: CREATE_TICKET_FIELD_ORDER,
  createFormSections: CREATE_TICKET_FORM_SECTIONS,

  /**
   * TRUE multi-field batch edit: select tickets → set one or more of the
   * bulk-editable fields (status/priority/category/assigned_to) → apply once via
   * POST /api/tickets/bulk. Overrides the default per-item GenericBulkEditModal.
   */
  editAllFieldsModal: TicketBulkEditModal,

  /** Default table column order: issue → reference → urgency → who → when */
  tableColumnOrder: [
    "title",
    "ticket_number",
    "status",
    "priority",
    "contact_id",
    "assigned_to",
    "due_date",
  ],

  /** Card view: issue → urgency → who → when */
  cardFaceFieldOrder: [
    "title",
    "status",
    "priority",
    "ticket_number",
    "contact_id",
    "assigned_to",
    "due_date",
  ],

  mobileDetailSections: [
    { id: "basic", label: "Basics" },
    { id: "status", label: "Status" },
    { id: "assignment", label: "Assignment" },
    { id: "time", label: "Time & Hours" },
    { id: "financial", label: "Financial & SLA" },
    { id: "notes", label: "Notes & Options" },
    { id: "timeline", label: "Timeline" },
    { id: "reporting", label: "Reporting" },
  ],

  fields: ticketFields,

  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },

  // Add detail page actions for table rows
  detailActions: ENHANCED_TICKET_DETAIL_CONFIG.actions,

  features: ticketFeatures,

  savedViews: ticketSavedViews,

  // Quick filters for table filtering
  quickFilters: TICKET_QUICK_FILTERS,

  // Custom filter hooks for saved views (can be populated later if needed)
  customFilterHooks: {},
};
